import { useState, useRef, useEffect } from 'react'
import { Plus, Search, Download, MoreVertical, Pencil, Trash2, QrCode, Link } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useCodigosQR, useCreateCodigoQR, useUpdateCodigoQR, useDeleteCodigoQR } from '../hooks/useCodigosQR'
import { previewQR } from '../api/codigos-qr'
import type { CodigoQR } from '../types'

function QRCard({
  qr,
  onEdit,
  onDelete,
}: {
  qr: CodigoQR
  onEdit: (qr: CodigoQR) => void
  onDelete: (qr: CodigoQR) => void
}) {
  const handleDownload = () => {
    const link = document.createElement('a')
    link.download = `${qr.nombre.replace(/\s+/g, '_')}.png`
    link.href = qr.qr_base64
    link.click()
  }

  return (
    <Card className="border border-gray-200 dark:border-gray-700">
      <CardContent className="p-4 flex flex-col items-center gap-3">
        <div className="bg-white p-2 rounded-lg border border-gray-100 dark:border-gray-700">
          <img
            src={qr.qr_base64}
            alt={`QR de ${qr.nombre}`}
            className="w-40 h-40 object-contain"
            draggable={false}
          />
        </div>

        <div className="w-full">
          <p className="font-semibold text-sm text-gray-900 dark:text-white truncate">{qr.nombre}</p>
          {qr.descripcion && (
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{qr.descripcion}</p>
          )}
          <a
            href={qr.url_destino}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs text-violet-600 dark:text-violet-400 hover:underline mt-1 truncate"
          >
            <Link className="size-3 shrink-0" />
            <span className="truncate">{qr.url_destino}</span>
          </a>
        </div>

        <div className="w-full flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
            onClick={handleDownload}
          >
            <Download className="size-3.5 mr-1.5" />
            Descargar
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="size-8 border-gray-300 dark:border-gray-600 shrink-0">
                <MoreVertical className="size-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(qr)}>
                <Pencil className="size-4 mr-2" />
                Editar
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-red-600 dark:text-red-400 focus:text-red-600"
                onClick={() => onDelete(qr)}
              >
                <Trash2 className="size-4 mr-2" />
                Eliminar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
    </Card>
  )
}

function QRFormDialog({
  open,
  onClose,
  initial,
}: {
  open: boolean
  onClose: () => void
  initial?: CodigoQR | null
}) {
  const isEdit = !!initial
  const [nombre, setNombre] = useState(initial?.nombre ?? '')
  const [descripcion, setDescripcion] = useState(initial?.descripcion ?? '')
  const [url, setUrl] = useState(initial?.url_destino ?? '')
  const [error, setError] = useState('')
  const [previewSrc, setPreviewSrc] = useState<string>(initial?.qr_base64 ?? '')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!open) return
    setNombre(initial?.nombre ?? '')
    setDescripcion(initial?.descripcion ?? '')
    setUrl(initial?.url_destino ?? '')
    setPreviewSrc(initial?.qr_base64 ?? '')
    setError('')
  }, [open, initial])

  useEffect(() => {
    if (!url.trim()) { setPreviewSrc(''); return }
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      try {
        const b64 = await previewQR(url.trim())
        setPreviewSrc(b64)
      } catch {
        // silently ignore preview errors
      }
    }, 500)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [url])

  const createMutation = useCreateCodigoQR()
  const updateMutation = useUpdateCodigoQR()
  const loading = createMutation.isPending || updateMutation.isPending

  const handleSubmit = async () => {
    if (!nombre.trim()) { setError('El nombre es requerido'); return }
    if (!url.trim()) { setError('La URL es requerida'); return }
    setError('')

    try {
      if (isEdit) {
        await updateMutation.mutateAsync({ id: initial!.id, payload: { nombre, descripcion, url_destino: url } })
      } else {
        await createMutation.mutateAsync({ nombre, descripcion, url_destino: url })
      }
      onClose()
    } catch {
      setError('Error al guardar. Verifica que la URL sea válida.')
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar QR' : 'Crear QR'}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-1.5">
            <Label htmlFor="qr-nombre">Nombre <span className="text-red-500">*</span></Label>
            <Input
              id="qr-nombre"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Ej: Web principal"
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="qr-url">URL de destino <span className="text-red-500">*</span></Label>
            <Input
              id="qr-url"
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://ejemplo.com"
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="qr-desc">Descripción</Label>
            <Textarea
              id="qr-desc"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Descripción opcional"
              rows={2}
            />
          </div>

          {error && <p className="text-sm text-red-500">{error}</p>}

          <div className="flex flex-col items-center gap-2 py-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">Vista previa</p>
            {previewSrc ? (
              <div className="bg-white p-2 rounded-lg border border-gray-200 dark:border-gray-700">
                <img src={previewSrc} alt="Vista previa QR" className="w-36 h-36 object-contain" />
              </div>
            ) : (
              <div className="w-36 h-36 flex items-center justify-center bg-white rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="text-center">
                  <QrCode className="size-8 text-gray-300 mx-auto mb-1" />
                  <p className="text-xs text-gray-400">Ingresa una URL</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-violet-800 hover:bg-violet-900 text-white"
          >
            {loading ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Crear QR'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function DeleteDialog({
  qr,
  onClose,
}: {
  qr: CodigoQR | null
  onClose: () => void
}) {
  const deleteMutation = useDeleteCodigoQR()

  const handleDelete = async () => {
    if (!qr) return
    await deleteMutation.mutateAsync(qr.id)
    onClose()
  }

  return (
    <Dialog open={!!qr} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Eliminar QR</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-600 dark:text-gray-400 py-2">
          ¿Eliminar el QR <strong>"{qr?.nombre}"</strong>? Esta acción no se puede deshacer.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={deleteMutation.isPending}>Cancelar</Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function CodigosQRPage() {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [formOpen, setFormOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<CodigoQR | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<CodigoQR | null>(null)
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  const { data, isLoading } = useCodigosQR({ search: debouncedSearch || undefined, page_size: 48 })

  const handleSearchChange = (value: string) => {
    setSearch(value)
    if (searchTimeout.current) clearTimeout(searchTimeout.current)
    searchTimeout.current = setTimeout(() => setDebouncedSearch(value), 350)
  }

  const handleEdit = (qr: CodigoQR) => {
    setEditTarget(qr)
    setFormOpen(true)
  }

  const handleCloseForm = () => {
    setFormOpen(false)
    setEditTarget(null)
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <QrCode className="size-6 text-violet-500" />
            Código QR
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Genera y administra tus códigos QR personales
          </p>
        </div>
        <Button
          onClick={() => setFormOpen(true)}
          className="bg-violet-800 hover:bg-violet-900 text-white"
        >
          <Plus className="size-4 mr-2" />
          Crear QR
        </Button>
      </div>

      <div className="relative mb-6 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
        <Input
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder="Buscar por nombre o URL..."
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500" />
        </div>
      ) : !data?.results?.length ? (
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <QrCode className="size-12 text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-gray-500 dark:text-gray-400 font-medium">
            {debouncedSearch ? 'No se encontraron QRs' : 'Aún no tienes códigos QR'}
          </p>
          {!debouncedSearch && (
            <Button variant="outline" className="mt-4" onClick={() => setFormOpen(true)}>
              <Plus className="size-4 mr-2" />
              Crear tu primer QR
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {data.results.map((qr) => (
            <QRCard key={qr.id} qr={qr} onEdit={handleEdit} onDelete={setDeleteTarget} />
          ))}
        </div>
      )}

      <QRFormDialog open={formOpen} onClose={handleCloseForm} initial={editTarget} />
      <DeleteDialog qr={deleteTarget} onClose={() => setDeleteTarget(null)} />
    </div>
  )
}
